import React, { useEffect, useState, useRef, Fragment } from 'react';
import { logger } from '../../utils/logger';
import { CheckIcon, XIcon, EyeIcon, ShieldCheckIcon, SaveIcon, LockIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Role, Permission, PermissionLevel, normalizeRoleName } from '../../types';
export function Permissions() {
  const {
    roles,
    permissions,
    getPermissionsByRole,
    updatePermissions
  } = useData();
  const {
    showToast
  } = useToast();
  const {
    confirm
  } = useConfirm();
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [rolePermissions, setRolePermissions] = useState<Record<string, Permission>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  // Marca el roleId que acabamos de guardar manualmente, para que el useEffect
  // de sincronización (disparado por el cambio del array global "permissions")
  // no pise el estado recién confirmado con una versión potencialmente desfasada
  // mientras la actualización del contexto todavía está propagándose.
  const justSavedRoleIdRef = useRef<string | null>(null);
  const modules = [{
    key: 'dashboard',
    label: 'Panel de Control',
    section: 'Dashboard'
  }, {
    key: 'dashboard_calendar',
    label: '↳ Calendario',
    section: 'Dashboard'
  }, {
    key: 'dashboard_stats',
    label: '↳ Estadísticas',
    section: 'Dashboard'
  }, {
    key: 'dashboard_pipeline',
    label: '↳ Pipeline de Ventas',
    section: 'Dashboard'
  }, {
    key: 'dashboard_appointments_by_user',
    label: '↳ Citas por Usuario',
    section: 'Dashboard'
  }, {
    key: 'dashboard_revenue_user',
    label: '↳ Ingresos por Usuario',
    section: 'Dashboard'
  }, {
    key: 'dashboard_revenue_doctor',
    label: '↳ Ingresos por Doctor',
    section: 'Dashboard'
  }, {
    key: 'dashboard_revenue_export',
    label: '↳ Exportar Ingresos (PDF/Excel)',
    section: 'Dashboard'
  }, {
    key: 'dashboard_product_revenue',
    label: '↳ Ingresos por Productos (Top 5)',
    section: 'Dashboard'
  }, {
    key: 'dashboard_recent_appointments',
    label: '↳ Citas Recientes',
    section: 'Dashboard'
  }, {
    key: 'dashboard_service_detail',
    label: '↳ Detalle de Servicios',
    section: 'Dashboard'
  }, {
    key: 'patients',
    label: 'Pacientes',
    section: 'Gestión Médica'
  }, {
    key: 'patients_insurance',
    label: '↳ Ver Seguros',
    section: 'Gestión Médica'
  }, {
    key: 'patient360',
    label: 'Cliente 360',
    section: 'Gestión Médica'
  }, {
    key: 'sessionPackages',
    label: 'Paquetes de Sesiones',
    section: 'Gestión Médica'
  }, {
    key: 'giftCertificates',
    label: 'Certificados de Regalo',
    section: 'Gestión Médica'
  }, {
    key: 'loyalty',
    label: 'Tarjetas de Fidelidad (7 visitas)',
    section: 'Gestión Médica'
  }, {
    key: 'doctors',
    label: 'Doctores',
    section: 'Gestión Médica'
  }, {
    key: 'appointments',
    label: 'Citas',
    section: 'Gestión Médica'
  }, {
    key: 'clinicalHistory',
    label: 'Historial Clínico',
    section: 'Gestión Médica'
  }, {
    key: 'products',
    label: 'Productos',
    section: 'Gestión Médica'
  }, {
    key: 'products_stats',
    label: '↳ Estadísticas de Productos',
    section: 'Gestión Médica'
  },
  // --- Servicios ---
  {
    key: 'services',
    label: 'Servicios (Gestión)',
    section: 'Servicios'
  }, {
    key: 'services_stats',
    label: '↳ Estadísticas y Reportes',
    section: 'Servicios'
  }, {
    key: 'offers',
    label: 'Ofertas',
    section: 'Servicios'
  },
  // --- Catálogos ---
  {
    key: 'insurance',
    label: 'Seguros',
    section: 'Catálogos'
  }, {
    key: 'specialties',
    label: 'Especialidades',
    section: 'Catálogos'
  },
  // --- CRM ---
  {
    key: 'contacts',
    label: 'Contactos',
    section: 'CRM'
  }, {
    key: 'companies',
    label: 'Empresas',
    section: 'CRM'
  }, {
    key: 'opportunities',
    label: 'Oportunidades',
    section: 'CRM'
  },
  // --- Seguimiento y Auditoría ---
  {
    key: 'followUpNotes',
    label: 'Notas de Seguimiento',
    section: 'Seguimiento y Auditoría'
  }, {
    key: 'auditLog',
    label: 'Bitácora',
    section: 'Seguimiento y Auditoría'
  }, {
    key: 'goals',
    label: 'Metas y Alertas',
    section: 'Seguimiento y Auditoría'
  },
  // --- Bot ---
  {
    key: 'botOmnichannel',
    label: 'Bot Omnichannel',
    section: 'Bot'
  },
  // --- Inteligencia ---
  {
    key: 'intelligence',
    label: 'Inteligencia y Analítica',
    section: 'Inteligencia'
  },
  // --- Comisiones ---
  {
    key: 'commissions',
    label: 'Comisiones',
    section: 'Comisiones'
  },
  // --- Configuración ---
  {
    key: 'settings',
    label: 'Configuración (General)',
    section: 'Configuración'
  }, {
    key: 'settings_profile',
    label: '↳ Perfil',
    section: 'Configuración'
  }, {
    key: 'settings_preferences',
    label: '↳ Preferencias',
    section: 'Configuración'
  }, {
    key: 'settings_notifications',
    label: '↳ Notificaciones',
    section: 'Configuración'
  }, {
    key: 'settings_security',
    label: '↳ Seguridad',
    section: 'Configuración'
  }, {
    key: 'settings_email',
    label: '↳ Correos',
    section: 'Configuración'
  }, {
    key: 'settings_verification',
    label: '↳ Verificación',
    section: 'Configuración'
  }, {
    key: 'settings_users',
    label: '↳ Usuarios',
    section: 'Configuración'
  }, {
    key: 'settings_roles_permissions',
    label: '↳ Roles y Permisos',
    section: 'Configuración'
  }, {
    key: 'settings_backups',
    label: '↳ Backups Automáticos',
    section: 'Configuración'
  }];

  // Group modules by section
  const groupedModules = modules.reduce((acc, module) => {
    const section = module.section || 'Otros';
    if (!acc[section]) acc[section] = [];
    acc[section].push(module);
    return acc;
  }, {} as Record<string, typeof modules>);
  const roleOptions = roles.map(r => ({
    value: r.id,
    label: r.displayName
  }));
  useEffect(() => {
    if (selectedRoleId) {
      // Si acabamos de guardar este mismo rol manualmente, el estado local ya
      // está al día con lo confirmado — no lo reconstruyamos desde "permissions"
      // (que puede tardar un instante en reflejar el cambio) para evitar que la
      // pantalla "se pegue" mostrando un valor viejo justo después de guardar.
      if (justSavedRoleIdRef.current === selectedRoleId) {
        justSavedRoleIdRef.current = null;
        return;
      }
      const perms = getPermissionsByRole(selectedRoleId);
      const permsMap: Record<string, Permission> = {};
      // Initialize with existing permissions
      if (perms.length > 0) {
        perms.forEach(p => {
          permsMap[p.module] = p;
        });
      }
      // Ensure all modules have an entry
      modules.forEach(m => {
        if (!permsMap[m.key]) {
          permsMap[m.key] = {
            id: crypto.randomUUID(),
            roleId: selectedRoleId,
            module: m.key,
            canCreate: false,
            canRead: false,
            canUpdate: false,
            canDelete: false,
            permissionLevel: 'none' as PermissionLevel,
            createdAt: new Date(),
            updatedAt: new Date()
          };
        }
      });
      setRolePermissions(permsMap);
      setHasChanges(false);
    }
  }, [selectedRoleId, permissions]);
  const getPermissionIcon = (level: PermissionLevel) => {
    switch (level) {
      case 'full':
        return <CheckIcon className="w-5 h-5 text-green-500 dark:text-green-400" data-id="element-4824" data-mp="1ecqvqjhtkqpp" />;
      case 'read':
        return <EyeIcon className="w-5 h-5 text-blue-500 dark:text-blue-400" data-id="element-4825" data-mp="223teq6juc6u" />;
      case 'own':
        return <ShieldCheckIcon className="w-5 h-5 text-amber-500 dark:text-amber-400" data-id="element-4826" data-mp="3targpboco9h1" />;
      case 'none':
        return <XIcon className="w-5 h-5 text-red-500 dark:text-red-400" data-id="element-4827" data-mp="3oh5ekdftbnzh" />;
    }
  };
  const getPermissionLabel = (level: PermissionLevel) => {
    switch (level) {
      case 'full':
        return 'Completo';
      case 'read':
        return 'Solo lectura';
      case 'own':
        return 'Propias';
      case 'none':
        return 'Sin acceso';
    }
  };
  const cyclePermission = (module: string) => {
    const levels: PermissionLevel[] = ['none', 'read', 'own', 'full'];
    const currentLevel = rolePermissions[module]?.permissionLevel || 'none';
    const nextLevel = levels[(levels.indexOf(currentLevel) + 1) % levels.length];
    setRolePermissions(prev => ({
      ...prev,
      [module]: {
        ...(prev[module] || {
          id: crypto.randomUUID(),
          roleId: selectedRoleId,
          module,
          createdAt: new Date(),
          updatedAt: new Date()
        }),
        permissionLevel: nextLevel,
        canCreate: nextLevel === 'full',
        canRead: nextLevel !== 'none',
        canUpdate: nextLevel === 'full' || nextLevel === 'own',
        canDelete: nextLevel === 'full'
      }
    }));
    setHasChanges(true);
  };
  const handleSave = async () => {
    if (!selectedRoleId) return;
    confirm({
      title: 'Guardar Permisos',
      message: '¿Estás seguro de que deseas guardar los cambios en los permisos?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        try {
          const permissionsToSave = Object.values(rolePermissions);
          await updatePermissions(selectedRoleId, permissionsToSave);
          const selectedRole = roles.find(r => r.id === selectedRoleId);
          if (selectedRole) {
            // Reflejamos de inmediato lo que acabamos de guardar en el estado local
            // del formulario. No dependemos de que el useEffect (disparado por el
            // cambio del array global "permissions") llegue a tiempo — así se evita
            // que la pantalla se quede mostrando valores viejos/"pegados" si vuelves
            // a editar justo después de guardar.
            const confirmedMap: Record<string, Permission> = {};
            permissionsToSave.forEach(p => {
              confirmedMap[p.module] = {
                ...p,
                roleId: selectedRoleId
              };
            });
            setRolePermissions(confirmedMap);
            const saved = localStorage.getItem('savidna-role-permissions');
            const allPermissions = saved ? JSON.parse(saved) : {};
            // Normalizamos para que "Recepción" se guarde como "recepcion"
            const normalizedName = normalizeRoleName(selectedRole.name);
            modules.forEach(m => {
              if (!allPermissions[m.key]) allPermissions[m.key] = {};
              // Guardamos el nivel (read, full, etc.) para este rol específico
              allPermissions[m.key][normalizedName] = confirmedMap[m.key]?.permissionLevel || 'none';
            });
            localStorage.setItem('savidna-role-permissions', JSON.stringify(allPermissions));
            // VITAL: Avisa al Dashboard que los permisos cambiaron
            window.dispatchEvent(new Event('permissions-updated'));
            setHasChanges(false);
            showToast({
              type: 'success',
              title: 'Permisos guardados',
              message: 'Los permisos fueron guardados correctamente.'
            });
            setSaveMessage('✅ Permisos guardados correctamente');
            setTimeout(() => setSaveMessage(''), 3000);
          }
        } catch (error) {
          logger.error('Error saving permissions:', error);
          showToast({
            type: 'error',
            title: 'Error al guardar',
            message: 'No se pudieron guardar los permisos.'
          });
          setSaveMessage('Error al guardar permisos');
        }
      }
    });
  };
  const selectedRole = roles.find(r => r.id === selectedRoleId);
  return <div className="space-y-6" data-id="element-4828" data-mp="2x9e5zmjbwx3g">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-4829" data-mp="326efk7fe0hzb">
        <div data-id="element-4830" data-mp="3pw8r8lnz00hz">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-4831" data-mp="3pwi9fn2rx3d4">
            <LockIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" data-id="element-4832" data-mp="3uvv4uw7pnirb" />
            Matriz de Permisos
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-4833" data-mp="3guir8rg0vt8l">
            Configura el nivel de acceso para cada módulo
          </p>
        </div>
        {hasChanges && <Button leftIcon={<SaveIcon className="w-4 h-4" data-id="element-4835" data-mp="25fm09c0a2tyl" />} onClick={handleSave} data-id="element-4834" data-mp="25fvmcoi7cgz0">
          
            Guardar Cambios
          </Button>}
      </div>

      {saveMessage && <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm animate-in fade-in slide-in-from-top-2" data-id="element-4836" data-mp="21xoiftu7hqkd">
          {saveMessage}
        </div>}

      <Card className="p-6" data-id="element-4837" data-mp="2buks42vuciqi">
        <div className="max-w-xl" data-id="element-4838" data-mp="26xu1wmqbo6rd">
          <Select label="Seleccionar Rol a Configurar" options={roleOptions} value={selectedRoleId} onChange={setSelectedRoleId} placeholder="Seleccione un rol..." data-id="element-4839" data-mp="26ylv5qwg5seh" />
          

          {selectedRole && <div className="mt-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-xl flex items-start gap-3" data-id="element-4840" data-mp="1sxjb1jp0is67">
              <ShieldCheckIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5" data-id="element-4841" data-mp="1o5rrht49xmpk" />
              <div data-id="element-4842" data-mp="10fnptranbx17">
                <p className="text-sm font-medium text-indigo-900 dark:text-indigo-200" data-id="element-4843" data-mp="10f5ju2y6m4g3">
                  Editando: {selectedRole.displayName}
                </p>
                <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-1" data-id="element-4844" data-mp="3uxprnnz9b14d">
                  {selectedRole.description || 'Sin descripción'}
                </p>
              </div>
            </div>}
        </div>
      </Card>

      {selectedRoleId ? <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-4845" data-mp="ciosq7jc34tc">
            {[{
          icon: CheckIcon,
          label: 'Acceso completo',
          color: 'text-green-500'
        }, {
          icon: EyeIcon,
          label: 'Solo lectura',
          color: 'text-blue-500'
        }, {
          icon: ShieldCheckIcon,
          label: 'Solo propias',
          color: 'text-amber-500'
        }, {
          icon: XIcon,
          label: 'Sin acceso',
          color: 'text-red-500'
        }].map((item, i) => <div key={i} className="flex items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm" data-id="element-4846" data-mp="2x9pcinp7y4eq" data-mpi={i}>
            
                <item.icon className={`w-5 h-5 ${item.color}`} data-id="element-4847" data-mp="2kxdixcxts8m5" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300" data-id="element-4848" data-mp="2fzbppz6oqswy">
                  {item.label}
                </span>
              </div>)}
          </div>

          <Card className="overflow-hidden" data-id="element-4849" data-mp="2aye7xnpl80vz">
            {/* Mobile view */}
            <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4850" data-mp="38n7t1mqrfj2p">
              {Object.entries(groupedModules).map(([sectionName, sectionModules]) => <Fragment key={sectionName} data-id="element-4851" data-mp="2yo8hnwem19c0">
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50" data-id="element-4852" data-mp="2uohwc87urlev">
                      <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400" data-id="element-4853" data-mp="2uqkjmkhmf0fw">
                        {sectionName}
                      </span>
                    </div>
                    {sectionModules.map(module => {
              const permission = rolePermissions[module.key];
              const level = permission?.permissionLevel || 'none';
              const isSubModule = module.label.startsWith('↳');
              return <div key={module.key} className={`flex items-center justify-between gap-3 px-4 py-3 ${isSubModule ? 'pl-8' : ''}`} data-id="element-4854" data-mp="1aues48bvni7g">
                      
                          <span className={`text-sm flex-1 min-w-0 truncate ${isSubModule ? 'text-gray-500 dark:text-gray-400' : 'font-medium text-gray-900 dark:text-white'}`} data-id="element-4855" data-mp="16qgslwin45dv">
                        
                            {module.label}
                          </span>
                          <button onClick={() => cyclePermission(module.key)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all border border-transparent hover:border-gray-200 flex-shrink-0" data-id="element-4856" data-mp="j0uu7ba3du6x">
                        
                            {getPermissionIcon(level)}
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap" data-id="element-4857" data-mp="deucmf8cu1ip">
                              {getPermissionLabel(level)}
                            </span>
                          </button>
                        </div>;
            })}
                  </Fragment>)}
            </div>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto" data-id="element-4858" data-mp="ibuhwnxdr08r">
              <table className="w-full text-sm text-left" data-id="element-4859" data-mp="w4ah1dn5ue4e">
                <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-4860" data-mp="s409hwjpzfnf">
                  <tr data-id="element-4861" data-mp="164b2p68i56br">
                    <th className="px-6 py-4 font-semibold" data-id="element-4862" data-mp="161f3zfotydm9">Módulo</th>
                    <th className="px-6 py-4 text-center font-semibold" data-id="element-4863" data-mp="115rcgtmvl8dl">
                      Nivel de Acceso
                    </th>
                    <th className="px-6 py-4 text-center font-semibold text-xs text-gray-400" data-id="element-4864" data-mp="28j8d5f9rkgki">
                      Crear
                    </th>
                    <th className="px-6 py-4 text-center font-semibold text-xs text-gray-400" data-id="element-4865" data-mp="2hiloyeld1h8e">
                      Leer
                    </th>
                    <th className="px-6 py-4 text-center font-semibold text-xs text-gray-400" data-id="element-4866" data-mp="2mekggb1zjfd1">
                      Editar
                    </th>
                    <th className="px-6 py-4 text-center font-semibold text-xs text-gray-400" data-id="element-4867" data-mp="1tr7t3yo033io">
                      Eliminar
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4868" data-mp="23i2i3asmsh7t">
                  {Object.entries(groupedModules).map(([sectionName, sectionModules]) => <Fragment key={sectionName} data-id="element-4869" data-mp="1b0yh5x2evxds">
                        <tr className="bg-gray-50/50 dark:bg-gray-800/30" data-id="element-4870" data-mp="1azf2uc0bfwen">
                          <td colSpan={6} className="px-6 py-3" data-id="element-4871" data-mp="13go10bjx9yct">
                            <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400" data-id="element-4872" data-mp="yicpzxhw5ht0">
                              {sectionName}
                            </span>
                          </td>
                        </tr>
                        {sectionModules.map(module => {
                  const permission = rolePermissions[module.key];
                  const level = permission?.permissionLevel || 'none';
                  const isSubModule = module.label.startsWith('↳');
                  return <tr key={module.key} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-4873" data-mp="aqyujiog163t">
                          
                              <td className={`px-6 py-4 font-medium text-gray-900 dark:text-white ${isSubModule ? 'pl-10 text-gray-500' : ''}`} data-id="element-4874" data-mp="wymb7xv7kqv">
                            
                                {module.label}
                              </td>
                              <td className="px-6 py-4 text-center" data-id="element-4875" data-mp="2cual018bh8nq">
                                <button onClick={() => cyclePermission(module.key)} className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all border border-transparent hover:border-gray-200 dark:hover:border-gray-600 w-full max-w-[140px] mx-auto" data-id="element-4876" data-mp="28tgxh8ysdvvd">
                              
                                  {getPermissionIcon(level)}
                                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300" data-id="element-4877" data-mp="1l60h94osprks">
                                    {getPermissionLabel(level)}
                                  </span>
                                </button>
                              </td>
                              {['canCreate', 'canRead', 'canUpdate', 'canDelete'].map(key => <td key={key} className="px-6 py-4 text-center" data-id="element-4878" data-mp="o84h1jofw4q">
                                  {permission?.[key as keyof Permission] ? <CheckIcon className="w-4 h-4 text-green-500 mx-auto" data-id="element-4879" data-mp="4mfj9qukynda" /> : <div className="w-1.5 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 mx-auto" data-id="element-4880" data-mp="341sowb5u9pfg" />}
                                </td>)}
                            </tr>;
                })}
                      </Fragment>)}
                </tbody>
              </table>
            </div>
          </Card>
        </> : <div className="py-12 text-center text-gray-400 bg-gray-50 dark:bg-gray-800/30 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-4881" data-mp="2lbb894ya6kag">
          <ShieldCheckIcon className="w-12 h-12 mx-auto mb-3 opacity-20" data-id="element-4882" data-mp="3sov7uv1k3oyn" />
          <p data-id="element-4883" data-mp="3nrbt7u0y5a66">
            Selecciona un rol arriba para comenzar a configurar sus permisos
          </p>
        </div>}
    </div>;
}